/**
 * Bottom sheet for one offline pack (song / collection / "allt mitt"): size estimate and
 * "Vista fyrir ónettengt" before saving; status, progress, "Sækja aftur", "Reyna aftur" and
 * "Fjarlægja" afterwards. Editors choose "Allar raddir" (default: own voice + whole choir).
 */
import { CloudAlert, CloudCheck, CloudDownload, CloudSync, LoaderCircle, RefreshCw, Trash2, type LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { roleAtLeast } from '../../shared/audience';
import { useMe } from '../auth/AuthProvider';
import { useToast } from '../components/toast';
import { Button, Sheet, Switch } from '../components/ui';
import { useI18n } from '../i18n';
import { formatSize, offlineErrorText, packStatusText, packTitle, percent } from './format';
import { useOfflineSnapshot, usePack } from './hooks';
import { previewPack, redownloadPack, removePack, retryPacks, savePack, syncManifest } from './manager';
import type { PackView } from './store';
import type { PackKind } from './types';

export const STATUS_ICON: Record<PackView['status'], LucideIcon> = {
  pending: CloudDownload,
  downloading: LoaderCircle,
  complete: CloudCheck,
  update: CloudSync,
  error: CloudAlert,
};

export function ProgressBar({ value, label }: { value: number; label: string }) {
  return (
    <div
      className="h-2.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)] ring-1 ring-[var(--line)]"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      aria-label={label}
    >
      <div className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-300" style={{ width: `${value}%` }} />
    </div>
  );
}

/** Status + progress of an existing pack (shared by the sheet and the storage list). */
export function PackProgress({ view }: { view: PackView }) {
  const { t, loc } = useI18n();
  const snap = useOfflineSnapshot();
  const Icon = STATUS_ICON[view.status];
  const active = view.status === 'downloading';
  const waiting = active ? snap.progress?.waiting : null;
  const bytesDone = active && snap.progress ? snap.progress.bytesDone : view.bytesDone;
  const tone =
    view.status === 'complete' ? 'text-[var(--ok-text)]' : view.status === 'error' ? 'text-[var(--danger-text)]' : view.status === 'update' ? 'text-amber-700 dark:text-amber-300' : 'text-[var(--accent)]';
  return (
    <div className="flex flex-col gap-2">
      <div className={`flex items-center gap-2 font-semibold ${tone}`}>
        <Icon className={`size-5 shrink-0 ${active ? 'animate-spin' : ''}`} aria-hidden />
        <span>{packStatusText(t, view)}</span>
        {view.status !== 'complete' && view.filesTotal > 0 ? (
          <span className="ml-auto text-[15px] font-medium text-[var(--muted)] tabular-nums">{t('offline.percent', { percent: percent(bytesDone, view.bytesTotal) })}</span>
        ) : null}
      </div>
      {view.status !== 'complete' && view.filesTotal > 0 ? <ProgressBar value={percent(bytesDone, view.bytesTotal)} label={packStatusText(t, view)} /> : null}
      <p className="text-[15px] text-[var(--muted)] tabular-nums">
        {view.status === 'complete'
          ? formatSize(view.bytesTotal, loc)
          : `${t('offline.files_progress', { done: view.filesDone, total: view.filesTotal })} · ${t('offline.storage.size_of', { done: formatSize(bytesDone, loc), total: formatSize(view.bytesTotal, loc) })}`}
      </p>
      {waiting ? <p className="text-[15px] text-[var(--muted)]">{t(waiting === 'network' ? 'offline.waiting_network' : 'offline.waiting_retry')}</p> : null}
      {view.status === 'error' && view.row.error ? <p className="text-[15px] text-[var(--danger-text)]">{offlineErrorText(t, view.row.error)}</p> : null}
      {active && !waiting ? <p className="text-[15px] font-medium text-[var(--ink-2)]">{t('offline.keep_open')}</p> : null}
    </div>
  );
}

interface SheetProps {
  open: boolean;
  onClose: () => void;
  kind: PackKind;
  refId: string | null;
  title: string;
}

/** Mounted only while open, so long song lists do not subscribe one sheet per row to the store. */
export function SavePackSheet(props: SheetProps) {
  return props.open ? <SavePackSheetBody {...props} /> : null;
}

function SavePackSheetBody({ open, onClose, kind, refId, title }: SheetProps) {
  const { t, loc } = useI18n();
  const toast = useToast();
  const me = useMe();
  const editor = !!me && roleAtLeast(me.role, 'editor');
  const pack = usePack(kind, refId);
  const snap = useOfflineSnapshot();
  const [allSections, setAllSections] = useState(pack?.row.allSections ?? false);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    // A size estimate needs a manifest; fetch one when there is none yet.
    if (!snap.manifestAt && navigator.onLine) void syncManifest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const display = packTitle(t, { kind, title });
  // Re-computed on every store change (useOfflineSnapshot), e.g. when the manifest arrives.
  const preview = previewPack(kind, refId, allSections);
  const coveredBy =
    kind === 'song' && refId && !pack ? snap.packs.filter((p) => p.row.id !== `song:${refId}` && p.songIds.includes(refId)).map((p) => packTitle(t, p.row)) : [];

  const run = async (fn: () => Promise<void>, done?: string) => {
    setBusy(true);
    try {
      await fn();
      if (done) toast.show(done, { tone: 'ok' });
      return true;
    } catch (e) {
      toast.show(offlineErrorText(t, e), { tone: 'error' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async (sections: boolean) => {
    const ok = await run(() => savePack({ kind, refId, title, allSections: sections }), pack ? undefined : t('offline.saving_started'));
    if (ok && !pack) onClose();
  };

  const footer = confirmRemove ? (
    <>
      <Button variant="secondary" block onClick={() => setConfirmRemove(false)}>
        {t('common.cancel')}
      </Button>
      <Button
        variant="danger"
        block
        icon={Trash2}
        busy={busy}
        onClick={() =>
          void run(() => removePack(pack!.row.id), t('offline.removed')).then((ok) => {
            if (ok) onClose();
          })
        }
      >
        {t('offline.remove')}
      </Button>
    </>
  ) : pack ? (
    <>
      <Button variant="secondary" block icon={Trash2} className="text-[var(--danger-text)]" onClick={() => setConfirmRemove(true)}>
        {t('offline.remove')}
      </Button>
      {pack.status === 'error' ? (
        <Button block icon={RefreshCw} busy={busy} onClick={() => void run(() => retryPacks(pack.row.id))}>
          {t('offline.retry')}
        </Button>
      ) : (
        <Button variant="secondary" block icon={RefreshCw} busy={busy} disabled={pack.status === 'downloading'} onClick={() => void run(() => redownloadPack(pack.row.id))}>
          {t('offline.redownload')}
        </Button>
      )}
    </>
  ) : (
    <>
      <Button variant="secondary" block onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button block icon={CloudDownload} busy={busy} onClick={() => void save(allSections)}>
        {t('offline.save_short')}
      </Button>
    </>
  );

  return (
    <Sheet open={open} onClose={() => !busy && onClose()} title={display} footer={footer}>
      <div className="flex flex-col gap-4 py-2">
        {confirmRemove ? (
          <div className="text-center">
            <p className="text-lg font-semibold">{t('offline.remove_confirm')}</p>
            <p className="mt-1 text-[var(--ink-2)]">{t('offline.remove_body')}</p>
          </div>
        ) : pack ? (
          <PackProgress view={pack} />
        ) : (
          <>
            <p className="text-[var(--ink-2)]">{kind === 'all' ? t('offline.save_all_desc') : t('offline.save')}</p>
            {preview ? (
              <p className="text-[17px] font-semibold tabular-nums">
                {t('offline.estimate', { size: formatSize(preview.bytes, loc) })}
                {kind !== 'song' ? ` · ${t('offline.songs', { count: preview.songs })}` : ''}
              </p>
            ) : snap.syncing ? (
              <p className="text-[var(--muted)]">{t('common.loading')}</p>
            ) : null}
            {coveredBy.length > 0 ? <p className="text-[15px] text-[var(--muted)]">{t('offline.covered_by', { names: coveredBy.join(', ') })}</p> : null}
          </>
        )}
        {editor && !confirmRemove ? (
          <div className="rounded-2xl border border-[var(--line)] px-4">
            <Switch
              checked={allSections}
              disabled={busy}
              label={t('offline.all_sections')}
              description={t('offline.all_sections_desc')}
              onChange={(v) => {
                setAllSections(v);
                if (pack) void save(v);
              }}
            />
          </div>
        ) : null}
        {!pack && !confirmRemove ? <p className="text-[15px] text-[var(--muted)]">{t('offline.keep_open')}</p> : null}
      </div>
    </Sheet>
  );
}
